import React, { useState } from 'react';
import { ShoppingCart, MessageCircle, X, Plus, Send, Copy, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const fmtTimestamp = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── WhatsApp Preview Modal ────────────────────────────────────────────────────
export function WhatsAppPreviewModal({ items, supplierName, storeName, onClose, onMarkSent }) {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const [editableItems, setEditableItems] = useState(
    items.map(it => ({ ...it, _key: it.action_id || Math.random() }))
  );

  const updateQty = (key, val) => {
    const qty = Math.max(1, parseInt(val, 10) || 1);
    setEditableItems(prev => prev.map(it => it._key === key ? { ...it, quantity: qty } : it));
  };

  const removeItem = (key) => setEditableItems(prev => prev.filter(it => it._key !== key));

  const addCustomRow = () => {
    setEditableItems(prev => [...prev, {
      _key: Math.random(),
      action_id: null,
      product_name: 'New Item',
      pack_size: '',
      quantity: 1,
    }]);
  };

  const updateField = (key, field, val) => {
    setEditableItems(prev => prev.map(it => it._key === key ? { ...it, [field]: val } : it));
  };

  const colW = { name: 22, pack: 10, qty: 5 };
  const pad = (str, len) => String(str).substring(0, len).padEnd(len);

  const messageText = [
    `Hello ${supplierName},`,
    '',
    `This is ${storeName} — purchase order dated ${today}:`,
    '',
    `${pad('Product', colW.name)} | ${pad('Pack Size', colW.pack)} | Qty`,
    `${'-'.repeat(colW.name)}-+-${'-'.repeat(colW.pack)}-+------`,
    ...editableItems.map(it =>
      `${pad(it.product_name, colW.name)} | ${pad(it.pack_size || '—', colW.pack)} | ${it.quantity}`
    ),
    '',
    'Please confirm availability and expected delivery date.',
    '',
    `Thank you,`,
    storeName,
  ].join('\n');

  const handleCopy = () => {
    navigator.clipboard.writeText(messageText).then(
      () => toast.success('Message copied to clipboard'),
      () => toast.error('Could not copy — please select and copy manually')
    );
  };

  const handleMarkSent = () => {
    const now = new Date().toISOString();
    localStorage.setItem('kf2_order_last_sent_at', now);
    onMarkSent(now);
    toast.success('Order marked as sent');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            <div>
              <h2 className="text-base font-semibold">Purchase Order Preview</h2>
              <p className="text-xs text-muted-foreground">To: {supplierName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-5">
          {/* Editable order table */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Order Items</p>
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-xs text-muted-foreground">
                    <th className="text-left px-3 py-2 font-medium">Product Name</th>
                    <th className="text-left px-3 py-2 font-medium w-28">Pack Size</th>
                    <th className="text-left px-3 py-2 font-medium w-24">Required Qty</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {editableItems.map(it => (
                    <tr key={it._key}>
                      <td className="px-3 py-2">
                        <input
                          value={it.product_name}
                          onChange={e => updateField(it._key, 'product_name', e.target.value)}
                          className="w-full text-sm border-0 outline-none bg-transparent focus:bg-muted/30 rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={it.pack_size || ''}
                          onChange={e => updateField(it._key, 'pack_size', e.target.value)}
                          placeholder="e.g. 1 kg"
                          className="w-full text-sm border-0 outline-none bg-transparent focus:bg-muted/30 rounded px-1 py-0.5 text-muted-foreground"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="1"
                          value={it.quantity}
                          onChange={e => updateQty(it._key, e.target.value)}
                          className="w-16 text-sm border border-border rounded px-2 py-0.5 outline-none focus:border-primary text-center"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => removeItem(it._key)}
                          className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 border-t border-border bg-muted/20">
                <button
                  onClick={addCustomRow}
                  className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add custom item
                </button>
              </div>
            </div>
          </div>

          {/* Message preview */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Message Preview</p>
            <textarea
              readOnly
              value={messageText}
              rows={Math.min(18, editableItems.length + 10)}
              className="w-full font-mono text-xs bg-muted/30 border border-border rounded-xl px-4 py-3 outline-none resize-none text-foreground/80 leading-relaxed"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
          >
            <Copy className="h-4 w-4" />
            Copy Message
          </button>
          <button
            onClick={handleMarkSent}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors font-medium"
          >
            <Send className="h-4 w-4" />
            Mark as Sent
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order Cart Panel ──────────────────────────────────────────────────────────
export function OrderCartPanel({ cart, onUpdateQty, onRemove, onClearAll, suppliers, selectedSupplier, onSupplierChange, onSend, lastSentAt }) {
  const itemCount = cart.length;

  return (
    <div className="border border-border rounded-xl bg-card flex flex-col h-fit sticky top-4">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Purchase Order</span>
          {itemCount > 0 && (
            <span className="text-xs font-bold px-1.5 py-0.5 bg-primary text-primary-foreground rounded-full">
              {itemCount}
            </span>
          )}
        </div>
        {itemCount > 0 && (
          <button
            onClick={onClearAll}
            className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Cart items */}
      <div className="flex-1 overflow-auto max-h-64 px-3 py-2 space-y-1.5">
        {itemCount === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6 leading-relaxed">
            Add low-stock items<br />from the list →
          </p>
        ) : (
          cart.map(item => (
            <div key={item.action_id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/30 group">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{item.product_name}</p>
                {item.pack_size && (
                  <p className="text-[10px] text-muted-foreground">{item.pack_size}</p>
                )}
              </div>
              <input
                type="number"
                min="1"
                value={item.quantity}
                onChange={e => onUpdateQty(item.action_id, Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-14 text-xs text-center border border-border rounded-md px-1.5 py-1 outline-none focus:border-primary"
              />
              <button
                onClick={() => onRemove(item.action_id)}
                className="p-1 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Supplier + Send */}
      <div className="px-3 py-3 border-t border-border space-y-2.5">
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Supplier</label>
          <select
            value={selectedSupplier}
            onChange={e => onSupplierChange(e.target.value)}
            className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-background text-foreground outline-none focus:border-primary"
          >
            <option value="">Select supplier…</option>
            {suppliers.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>

        <button
          onClick={onSend}
          disabled={itemCount === 0 || !selectedSupplier}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Send Order via WhatsApp
        </button>

        {lastSentAt && (
          <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            Last sent: {fmtTimestamp(lastSentAt)}
          </p>
        )}
      </div>
    </div>
  );
}
